import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, Inject, PLATFORM_ID, AfterViewInit, ViewChild, ElementRef, afterNextRender, HostListener, ChangeDetectorRef } from '@angular/core';
//import { Dropdown, Offcanvas } from 'bootstrap';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { catchError, Subscription } from 'rxjs';
import { Globals } from 'src/app/globals';
import { BreakpointService } from 'src/app/services/breakpoint.service';
import { ConsentService } from 'src/app/services/consent.service';
import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';  
import { Data2Service } from 'src/app/services/data2/data2.service';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { AnalyticsService } from 'src/app/services/analytics.service';
import { activeSeasonalHome } from 'src/app/pages/home-seasonal/seasonal-home.schedule';

const COIN_LOGO = 'https://services.ifa.com/art/images/3689_72x72_c_ifa-coin-logo-80s-transparent.webp';

interface NavigationSection {
  title: string;
  columns: { title: string; links: [string, string, ('_blank')?][] }[];
}

@Component({
  selector: 'app-navigation',
  templateUrl: './navigation.component.html',
  styleUrls: ['./navigation.component.scss'],
  imports: [CommonModule, RouterModule, FontAwesomeModule]
})
export class NavigationComponent implements OnInit, OnDestroy, AfterViewInit{

  @ViewChild('searchTerm', {static:false}) searchTerm!:ElementRef;
  @ViewChild('offcanvasRight', {static:false}) offcanvasRight!:ElementRef;
  @Input() accountData:any = {};  
  @Input() loginDisplay = false;
  @Output() login = new EventEmitter;
  @Output() logout = new EventEmitter;
  logoSrc = COIN_LOGO;
  offcanvas:any|undefined;
  subscriptions: Array<Subscription> = [];
  
  mainMenuMobileActiveId:string = 'home';
  bootstrap:any;
  activeMenu: number | null = null;
  private hoverOpenTimer?: ReturnType<typeof setTimeout>;
  private hoverCloseTimer?: ReturnType<typeof setTimeout>;
  private hoverOpenedMenu: number | null = null;
  /** Hover intent: how long the mouse must rest on a label before its dropdown opens (none is open yet). */
  static readonly HOVER_OPEN_DELAY_MS = 300;
  activeMobileMenu: number | null = null;
  searchOpen = false;
  mobileOpen = false;
  private mobileTransition: 'opening' | 'closing' | null = null;
  private pendingMobileClose = false;
  private restoreMobileFocus = true;
  private mobileOpenRequest = 0;

  readonly navigationSections: NavigationSection[] = [
    { title: 'Our Services', columns: [
      { title: 'Individuals & Families', links: [
        ['Overview', '/individuals-families'],
        ['Risk Capacity Survey', '/survey'], ['Charts', '/charts'], ['Videos', '/videos'], ['Articles', '/articles'], 
        ['Wealth Management', '/services#investmentadvisory'], ['Financial Planning', '/services#financialplanning'],
        ['Retirement Planning', '/services#retirementplanning'], ['Our Advisors', '/about/advisors']
      ] },
      { title: 'Outsourced Chief Investment Officer', links: [
        ['Overview', '/ocio-overview', '_blank'], ['Who We Serve', '/ocio#ocio-start', '_blank'],
        ['Investment Approach', '/ocio#ocio-model', '_blank'], ['Consultants', '/ocio/team', '_blank'],
        ['Institutional', 'https://ifainstitutional.com/', '_blank']
      ] },
      { title: 'Employer Retirement Plans', links: [
        ['Overview', '/employer-retirement-plans'],
        ['Retirement Plan Services', '/retirement-plans'], ['Plan Sponsors', '/retirement-plans/plan-sponsors'],
        ['Plan Participants', '/retirement-plans/plan-participants']
      ] },
      { title: 'Tax Planning & Preparation', links: [
        ['Overview', '/tax-planning-preparation'],
        ['Tax Services', '/taxes'], ['Tax Planning', '/taxes/services'], ['Tax Preparation', '/taxes/preparation']
      ] }
    ] },
    { title: 'Portfolios', columns: [
      { title: 'Find Your Portfolio', links: [
        ['Risk Capacity Survey', '/survey'], ['Matching People with Portfolios', '/portfolios/matching-people-with-portfolios'],
        ['Portfolio Overview', '/portfolios'], ['Risk & Return', '/portfolios/risk-and-return']
      ] },
      { title: 'IFA Index Portfolios', links: Array.from({ length: 20 }, (_, index): [string, string] => [
        `Portfolio ${100 - index * 5}`, `/portfolios/${100 - index * 5}`
      ]) },
      { title: 'Explore', links: [
        ['Fund Portfolios', '/fund-portfolios'], ['Index Portfolios', '/index-portfolios'], ['Sustainable Portfolios', '/sustainability'], ['Charts', '/charts']
      ] },
      { title: 'Disclosures', links: [
        ['Index Descriptions', '/disclosures/index-data'], ['Fund Descriptions', '/disclosures/fund-data'],
        ['Back-testing Disclosures', '/disclosures']
      ] }
    ] },
    { title: 'Learn', columns: [
      { title: 'Investor Education', links: [
        ['The 12 Steps: Introduction', '/12steps/introduction'],
        ...['Active Investors', 'Nobel Laureates', 'Stock Pickers', 'Time Pickers', 'Manager Pickers',
          'Style Drifters', 'Silent Partners', 'Riskese', 'History', 'Risk Capacity', 'Risk Exposure', 'Invest and Relax']
          .map((title, index): [string, string] => [`Step ${index + 1}: ${title}`, `/12steps/step${index + 1}`]),
        ['Appendix', '/12steps/appendix'], ['Articles', '/articles'], ['Glossary', '/glossary']
      ] },
      { title: 'Videos & Films', links: [
        ['Video Library', '/videos'], ['Tune Out the Noise', '/tune-out-the-noise'],
        ['Index Funds: The Movie', '/indexfundsthemovie'], ['Investing: The Evidence', '/investingtheevidence'],
        ['Pioneers of Probability', '/pioneers-of-probability'],
        ['The Academic Papers that Changed Investing', '/the-papers-that-changed-investing'],
        ['David Booth', '/davidboothinterviews'], ['Mark Hebner', '/markhebnerinterviews'],
        ['Eugene Fama', '/eugenefamainterviews'], ['Kenneth French', '/kennethfrenchinterviews'],
        ['Harry Markowitz', '/harrymarkowitzinterviews'], ["Gerard O'Reilly", '/gerardoreillyinterviews'],
        ['Scott Bosworth', '/scottbosworthinterviews'], ['Apollo Lupescu', '/apollolupescuinterviews']
      ] },
      { title: 'Research & Resources', links: [
        ['Charts', '/charts'], ['Academic Papers', '/academic-papers'], ['Index Funds Book', '/books'],
        ['Book Library', '/book-library'], ['Quotes', '/quotes'], ['IFA Art Gallery', '/gallery'],
        ['IFA Brochure', 'https://www.ifa.com/pdfs/ifa_brochure.pdf', '_blank'],
        ['Academic Advantage', 'https://www.ifa.com/pdfs/the-academic-advantage.pdf', '_blank'],
        ['Petition', '/petition']
      ] },
      { title: 'Probability Education', links: [
        ['Galton Board Models', '/galton-board'], ['Proficiency Test', '/galtonboard/proficiency-test'],
        ['STEM', '/galtonboard/stem'], ['Galton Board Videos', '/galtonboard/videos'],
        ['Galton Board Photos', '/galtonboard/gallery'], ['Galton Board Articles', '/galtonboard/articles'],
        ['Bell Curves', '/galtonboard/probabilityexamplesinlife'], ['MarketCoins', '/coins'],
        ['Coin Flipper', '/calculators/coin-flip'], ["Pascal's Triangle", '/coins/pascals-triangle'],
        ['IFA Store', 'https://store.ifa.com/', '_blank']
      ] }
    ] },
    { title: 'Tools', columns: [
      { title: 'Investment Tools', links: [
        ['Risk Capacity Survey', '/survey'], ['IFA Index Calculator', '/calculator'],
        ['Fee Calculator', '/fee-calculator'],
        ['Private Equity True Return', '/calculators/private-equity-true-return'],
        ['Venture Capital True Return', '/calculators/venture-capital-true-return'],
        ['ETF & Mutual Fund Luck vs. Skill Analyzer', '/calculators/etf-and-mutual-fund-analyzer']
      ] },
      { title: 'Retirement Tools', links: [
        ['Retirement Calculator', '/retirement-analyzer'], ['Target Date Calculator', '/retirement-plans/target-date-comparison'],
        ['Retirement Income', '/calculators/retirement-income'], ['401(k) Calculator', '/calculators/401k-savings'],
        ['Roth IRA Conversion', '/calculators/roth-ira'], ['Surviving Spouse IRA', '/calculators/surviving-spouse-ira-rmd-calculator'],
        ['SB Retirement Contributions', '/calculators/small-business-retirement-contributions-estimator']
      ] },
      { title: 'Education Tools', links: [
        ['Markov Chain Calculator', '/calculators/markov-chain'], ['Monte Carlo Spending Policy', '/calculators/monte-carlo-spending-policy'],
        ['T-Statistic Calculator', '/calculators/t-stat-calculator'], ['Standard Error of the Mean', '/calculators/standard-error-of-the-mean'],
        ['College Planning', '/college-savings'], ['Net Worth', '/calculators/net-worth'], ['Net Income', '/calculators/net-income']
      ] },
      { title: 'More Resources', links: [
        ['FinPlan Surveys', '/questionnaire', '_blank'], ['IFA App', '/app'],
        ['Estate Planning Attorneys', '/estate-planning'], ['Senior Care Advisors', '/senior-care'],
        ['Investment Charts', '/charts'], ['Articles', '/articles'], ['Videos', '/videos']
      ] }
    ] },
    { title: 'About IFA', columns: [
      { title: 'Our Firm', links: [
        ['Awards', '/awards'], ['About IFA', '/about'], ['Value of IFA', '/about/value-of-ifa'],
        ['Investment Principles', '/about/investment-principles'], ['Our Fiduciary Role', '/about/our-fiduciary-role']
      ] },
      { title: 'Our People', links: [
        ['Wealth Advisors', '/about/advisors'], ['Client Service Specialists', '/about/client-service-specialists'],
        ['Management', '/about/management'], ['Taxes Department', '/taxes/about'],
        ['Financial Planning Department', '/about/financial-planning-department']
      ] },
      { title: 'Work with IFA', links: [
        ['Schedule a Conversation', '/find-an-advisor'], ['Contact', '/contact'],
        ['Open an Account', '/open-account'], ['Careers', '/careers']
      ] },
      { title: 'Information', links: [
        ['Fees', '/fees'], ['Form CRS', 'https://www.ifa.com/pdfs/ifa-form-crs.pdf', '_blank'],
        ['Disclosures', '/disclosures']
      ] }
    ] }
  ];

  constructor( 
    private router: Router, 
    public globals:Globals, 
    public bps:BreakpointService, 
    public consentService:ConsentService, 
    public data2Service:Data2Service,
    @Inject(PLATFORM_ID) private platformId: Object,
    private analyticsService:AnalyticsService,
    private elementRef: ElementRef<HTMLElement>,
    private changeDetector: ChangeDetectorRef
    // @Inject(DOCUMENT) private _document: Document,
  ) { 
   //console.log("navigation constructor")

    this.updateLogo(this.router.url);
    this.subscriptions.push(this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.updateLogo(event.urlAfterRedirects);
        this.closeNavigation();
        this.searchOpen = false;
      }
    }));

   
  }

  // A live seasonal homepage can swap in its own logo, on "/" only (see seasonal-home.schedule.ts).
  private updateLogo(url: string): void {
    const path = (url || '').split('?')[0].split('#')[0];
    const seasonalLogo = path === '/' ? activeSeasonalHome()?.logo : undefined;
    this.logoSrc = seasonalLogo || COIN_LOGO;
  }
//   ngOnChanges(changes: SimpleChanges): void {
//     if(changes['loginDisplay'].currentValue){
//       setTimeout(()=>{
// //        console.log("Navigation Component Changes",changes);
//         this.InitializeDropDown();
//       },1000);
//     }
 
//   }
  ngOnDestroy(): void {
    this.cancelHoverTimers();
    this.subscriptions.forEach(x=>x.unsubscribe());
    this.disposeOffCanvas();
  }

  ngOnInit(): void {   
  }
  adwordsEvent(){
    this.analyticsService.event('conversion', {'send_to': 'AW-1072658350/zb8PCI6p9_waEK7vvf8D' });//phone number clicked
  }
  _login(){
    this.login.emit();
  }
  _logout(){
    this.logout.emit();
    this.loginDisplay=false;
  }

  async ngAfterViewInit() {
    if (isPlatformBrowser(this.platformId)) {  
      this.bootstrap = await import("bootstrap");     
    }
 }

 branchClick(evt:any){
  //console.log("branchClick",evt.target);
  var branch = evt.target.closest(".branch");
  branch.classList.add("branchClicked");
  setTimeout(()=>{
      branch.classList.remove("branchClicked");
  },2000)
 }



 
  submenu(id:any,direction:string=""){   
    
   //console.log(id,direction)
    if(direction==".."){
//      console.log(id.srcElement)
      id.srcElement?.closest(".hasSub").classList.toggle("active");
    } 
    else{
//     console.log(id.currentTarget)
     id.target?.classList.toggle("active");
    }
    id.stopPropagation();
    id.preventDefault();
  
    return false; //stopPropagation

      // document.getElementById(this.mainMenuMobileActiveId)?.classList.toggle("active");
      // this.mainMenuMobileActiveId=id;
      // document.getElementById(this.mainMenuMobileActiveId)?.classList.toggle("active");      
  }

  mobileMenuClick(event:any){
    //console.log("mobileMenuClick",event,event.target?.attributes, event.target?.className);

    var className = event.target?.className;
//    console.log("className",className)
    if(className && className.indexOf && className.indexOf("hasSub")>-1){     
      event.target?.classList.toggle("active");
      event.stopPropagation();
      event.preventDefault();
      return false;
    }
    else if(className && className.indexOf &&  className.indexOf("btn-success")>-1){
      event.srcElement?.closest(".hasSub").classList.toggle("active");
      event.stopPropagation();
      event.preventDefault();
      return false;
    }
    

    var attrs = event.target?.attributes;
    if(!attrs || attrs.length<1){
      console.log("attrs",attrs)
      event.stopPropagation();
      event.preventDefault();
      return false;
    }
    var attributes = [];
    for (var i=0;i<attrs.length;i++){
      var att = attrs[i];
      attributes.push(att.nodeName);
  }
//    console.log("attributes",attributes,attributes.indexOf("routerlink"))
    if(attributes && attributes.length > 0){
      if(attributes.indexOf("routerlink")>0 || attributes.indexOf("href")>0){
        this.closeOffCanvas();
      }
      else{
        var hasSub = event.srcElement?.closest(".hasSub");
//        console.log("hasSub",hasSub)
        if(hasSub){
          hasSub.classList.toggle("active");
        }
      }     
      //this.offcanvas.dispose();
    }

    var localURL = true;

    if(event.target && event.target.attributes){   
      if(event.target.attributes.href && event.target.attributes.target){
        localURL=false
      }

    }

    if(localURL){
      event.stopPropagation();
      event.preventDefault();
      return false;
    }
    else{
      return true;
    }
  }

  searchIFA() {
    const searchTermValue = this.searchTerm?.nativeElement.value.trim(); 
    //console.log("searchIFA",searchTermValue)
    if(searchTermValue?.length>1){
      this.router.navigate(['/search'], { queryParams: { q: searchTermValue } });
    }
  }

  async openOffCanvas(){
    if (!isPlatformBrowser(this.platformId)) return;
    if (this.mobileOpen || this.mobileTransition) return;
    const request = ++this.mobileOpenRequest;
    this.closeMenu();
    this.searchOpen = false;
    this.searchTerm?.nativeElement.blur();
    if (!this.bootstrap) this.bootstrap = await import('bootstrap');
    if (request !== this.mobileOpenRequest || !window.matchMedia('(max-width: 1120px)').matches) return;
    this.initOffCanvas();
    this.offcanvas?.show();
  }
  async closeOffCanvas(restoreFocus = true){
    ++this.mobileOpenRequest;
    if (!restoreFocus) this.restoreMobileFocus = false;
    this.offcanvas?.hide();
  }
  initOffCanvas(){
    const element = this.offcanvasRight?.nativeElement;
    if (!this.offcanvas && this.bootstrap && element) {
      this.offcanvas = new this.bootstrap.Offcanvas(element);
      element.addEventListener('show.bs.offcanvas', this.onMobileShow);
      element.addEventListener('shown.bs.offcanvas', this.onMobileShown);
      element.addEventListener('hide.bs.offcanvas', this.onMobileHide);
      element.addEventListener('hidden.bs.offcanvas', this.onMobileHidden);
    }
  }
  disposeOffCanvas(){
      ++this.mobileOpenRequest;
      if(this.offcanvas){        
        const element = this.offcanvasRight?.nativeElement;
        element?.removeEventListener('show.bs.offcanvas', this.onMobileShow);
        element?.removeEventListener('shown.bs.offcanvas', this.onMobileShown);
        element?.removeEventListener('hide.bs.offcanvas', this.onMobileHide);
        element?.removeEventListener('hidden.bs.offcanvas', this.onMobileHidden);
        this.offcanvas.dispose();
        this.offcanvas=undefined;
      }    
  }

  private onMobileShow = () => {
    this.mobileOpen = true;
    this.mobileTransition = 'opening';
    this.pendingMobileClose = false;
    this.restoreMobileFocus = true;
  };
  private onMobileShown = () => {
    this.mobileTransition = null;
    if (this.pendingMobileClose) {
      this.pendingMobileClose = false;
      this.offcanvas?.hide();
    }
  };
  private onMobileHide = (event: Event) => {
    if (this.mobileTransition === 'opening') {
      event.preventDefault();
      this.pendingMobileClose = true;
      return;
    }
    this.mobileTransition = 'closing';
  };
  private onMobileHidden = () => {
    this.mobileTransition = null;
    this.mobileOpen = false;
    this.activeMobileMenu = null;
    if (this.restoreMobileFocus && window.matchMedia('(max-width: 1120px)').matches) {
      this.elementRef.nativeElement.querySelector<HTMLButtonElement>('#ifa-mobile-toggle')?.focus({ preventScroll: true });
    }
  };

  toggleMenu(index: number, event?: MouseEvent) {
    if (this.isReplayedClick(event)) return;
    this.searchOpen = false;
    this.cancelHoverTimers();
    // A mouse click right after hover-open keeps the menu open; keyboard clicks (detail 0) still toggle.
    if (event && event.detail > 0 && this.hoverOpenedMenu === index && this.activeMenu === index) {
      this.hoverOpenedMenu = null;
      return;
    }
    this.hoverOpenedMenu = null;
    this.activeMenu = this.activeMenu === index ? null : index;
  }

  hoverOpenMenu(index: number, event: PointerEvent) {
    if (!this.canHoverMenus(event)) return;
    this.cancelHoverTimers();
    const open = () => {
      this.searchOpen = false;
      if (this.activeMenu !== index) this.hoverOpenedMenu = index;
      this.activeMenu = index;
    };
    // Once a dropdown is open, moving to a neighboring label switches instantly.
    if (this.activeMenu !== null) {
      open();
      return;
    }
    // Otherwise wait, so a pointer just passing over the header doesn't pop a menu open.
    this.hoverOpenTimer = setTimeout(() => {
      this.hoverOpenTimer = undefined;
      open();
      this.changeDetector.markForCheck();
    }, NavigationComponent.HOVER_OPEN_DELAY_MS);
  }

  hoverCloseMenu(event: PointerEvent) {
    if (!this.canHoverMenus(event)) return;
    this.cancelHoverTimers();
    // Short delay so the pointer can cross from the label into the panel.
    this.hoverCloseTimer = setTimeout(() => {
      this.hoverCloseTimer = undefined;
      this.hoverOpenedMenu = null;
      this.activeMenu = null;
      this.changeDetector.markForCheck();
    }, 250);
  }

  /** Cancels a pending hover open and a pending hover close (clicks, Escape and leaving all go through here). */
  cancelHoverTimers() {
    if (this.hoverOpenTimer) {
      clearTimeout(this.hoverOpenTimer);
      this.hoverOpenTimer = undefined;
    }
    if (this.hoverCloseTimer) {
      clearTimeout(this.hoverCloseTimer);
      this.hoverCloseTimer = undefined;
    }
  }

  // withEventReplay() can deliver a tap made during hydration twice, which would toggle a menu open and straight back closed.
  private lastToggleClick?: Event;
  private isReplayedClick(event?: Event): boolean {
    if (!event) return false;
    const last = this.lastToggleClick;
    if (last && (event === last || (event.type === last.type && event.timeStamp === last.timeStamp))) return true;
    this.lastToggleClick = event;
    return false;
  }

  // Uses the event's pointerType: some browsers (VS Code's, touchscreen laptops) report hover:none for a real mouse.
  private canHoverMenus(event: PointerEvent): boolean {
    return isPlatformBrowser(this.platformId)
      && event.pointerType === 'mouse'
      && window.matchMedia('(min-width: 1121px)').matches;
  }

  closeMenu(restoreFocus = false) {
    this.cancelHoverTimers();
    const index = this.activeMenu;
    this.activeMenu = null;
    if (restoreFocus && index !== null) {
      this.elementRef.nativeElement.querySelector<HTMLButtonElement>(`#ifa-menu-toggle-${index}`)?.focus();
    }
  }

  focusMenu(index: number) {
    this.activeMenu = index;
    this.searchOpen = false;
    setTimeout(() => {
      this.elementRef.nativeElement.querySelector<HTMLAnchorElement>(`#ifa-mega-${index} a`)?.focus();
    });
  }

  toggleSearch(event?: Event) {
    if (this.isReplayedClick(event)) return;
    this.closeMenu();
    this.searchOpen = !this.searchOpen;
    if (this.searchOpen) {
      this.changeDetector.detectChanges();
      this.searchTerm?.nativeElement.focus({ preventScroll: true });
    }
  }

  // Drops focus after a click so the :focus-within dropdown doesn't stay open after navigating.
  blurTarget(event: Event) {
    (event.currentTarget as HTMLElement | null)?.blur();
  }

  closeNavigation() {
    this.closeMenu();
    this.closeOffCanvas(false);
  }

  @HostListener('document:click', ['$event'])
  @HostListener('document:focusin', ['$event'])
  onOutsideNavigation(event: Event) {
    if (!this.elementRef.nativeElement.contains(event.target as Node)) this.closeMenu();
  }

  @HostListener('document:keydown.escape')
  onNavigationEscape() {
    this.closeMenu(true);
    if (this.searchOpen) {
      this.searchOpen = false;
      this.elementRef.nativeElement.querySelector<HTMLButtonElement>('#ifa-search-toggle')?.focus();
    }
  }

  @HostListener('window:resize')
  onNavigationResize() {
    if (window.matchMedia('(max-width: 1120px)').matches) {
      this.closeMenu();
    } else {
      this.closeOffCanvas(false);
    }
  }

  _clearStorage(){
    if (isPlatformBrowser(this.platformId)) {  
      // Safe to use document here  
      document.cookie.split(";").forEach(function(c) { document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/"); });
      window.localStorage.clear();
      window.sessionStorage.clear();   
    }
  }

  @HostListener('window:beforeunload', ['$event'])
  beforeUnloadHandler(event: BeforeUnloadEvent) {

    //POST sessionStorage values  
    var user = sessionStorage.getItem('email');
    
    //user = 'me';

    if(user != null)
    {
        var journey = '';

        sessionStorage.removeItem('email');

        for(var i=0; i<sessionStorage.length; i++)
        {
          var timestampKey = sessionStorage.key(i);
          var page = sessionStorage.getItem(timestampKey ? timestampKey : 'unknown');

          if(page == 'unknown' || page != null && page.indexOf('/') >= 0)
          {
            journey += page + ':' + timestampKey + ',';
          }
          
        }

        var now = new Date();
        journey += 'abandon' + ':' + now.toISOString();

        this.data2Service.visitorLogSave$({
          "user_email":user,
          "pagelog":journey,
        }).subscribe((resp:any)=>{
          //console.log("logged resp",resp)
        })
    }
  }

}
